import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Heading, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Checkbox, 
  Select, 
  Input, 
  Textarea,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Divider,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
  Flex,
  Spacer,
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  Stack,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  useColorModeValue,
  Progress
} from '@chakra-ui/react';
import { ChevronDownIcon, StarIcon, DownloadIcon, CheckIcon, WarningIcon, ArrowBackIcon, RepeatIcon, ViewIcon, ChatIcon } from '@chakra-ui/icons';
import { useFindMany, useAction, useSession, useGlobalAction } from '@gadgetinc/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

const AdminFineTuning = () => {
  const session = useSession(api);
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen: isQualityOpen, onOpen: onQualityOpen, onClose: onQualityClose } = useDisclosure();
  const { isOpen: isExportOpen, onOpen: onExportOpen, onClose: onExportClose } = useDisclosure();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // State for filters
  const [filters, setFilters] = useState({
    reviewStatus: '',
    qualityRating: '',
    contentType: '',
    trainingFlag: '',
    dateFrom: '',
    dateTo: '',
    search: '',
    assistantId: '',
    dataSource: 'both', // 'legacy', 'agent', 'both'
    agentType: ''
  });

  // State for selected messages
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // State for quality assessment modal
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [qualityRating, setQualityRating] = useState(0);
  const [adminFeedback, setAdminFeedback] = useState('');
  const [contentType, setContentType] = useState('');
  const [trainingFlag, setTrainingFlag] = useState(true);

  // State for export options
  const [exportOptions, setExportOptions] = useState({
    exportType: 'standard',
    reverseQuestionAnswer: false,
    minQualityScore: null,
    maxQualityScore: null,
    dateFrom: '',
    dateTo: '',
    reviewStatus: 'approved',
    assistantId: '',
    dataSource: 'both'
  });

  // State for fine-tuning job management
  const [fineTuningState, setFineTuningState] = useState({
    batchProcessing: false,
    jobMonitoring: false,
    lastBatchResult: null,
    lastJobMonitorResult: null
  });

  // Build filter object for API (legacy messages)
  const buildLegacyFilter = () => {
    const filter = {};
    
    if (filters.reviewStatus) {
      filter.reviewStatus = { equals: filters.reviewStatus };
    }
    
    if (filters.qualityRating) {
      filter.qualityRating = { equals: parseInt(filters.qualityRating) };
    }
    
    if (filters.contentType) {
      filter.contentType = { equals: filters.contentType };
    }
    
    if (filters.trainingFlag !== '') {
      filter.trainingFlag = { equals: filters.trainingFlag === 'true' };
    }
    
    if (filters.dateFrom) {
      filter.createdAt = { ...filter.createdAt, greaterThanOrEqual: filters.dateFrom };
    }
    
    if (filters.dateTo) {
      filter.createdAt = { ...filter.createdAt, lessThanOrEqual: filters.dateTo };
    }

    if (filters.assistantId) {
      filter.thread = { 
        assistant: { 
          id: { equals: filters.assistantId } 
        } 
      };
    }

    console.log('Legacy filter:', JSON.stringify(filter, null, 2));
    return filter;
  };

  // Fetch legacy messages
  const [{ data: messages, fetching: messagesFetching, error: messagesError }] = useFindMany(
    api.message,
    {
      filter: filters.dataSource === 'agent' ? {} : buildLegacyFilter(),
      search: filters.search && filters.dataSource !== 'agent' ? filters.search : undefined,
      sort: { createdAt: 'Descending' },
      skip: filters.dataSource === 'agent',
      select: {
        id: true,
        createdAt: true,
        userMessage: true,
        assistantMessage: true,
        reviewStatus: true,
        qualityRating: true,
        contentType: true,
        trainingFlag: true,
        flagReason: true,
        reviewedAt: true,
        adminFeedback: { markdown: true },
        user: { id: true, email: true, firstName: true, lastName: true },
        thread: { id: true, description: true },
        reviewedBy: { id: true, email: true }
      }
    }
  );

  // Fetch assistants for filtering
  const [{ data: assistants, fetching: assistantsFetching }] = useFindMany(
    api.assistant,
    {
      select: {
        id: true,
        name: true,
        description: true,
        assistantType: true
      },
      sort: { name: 'Ascending' }
    }
  );

  // Fetch agent configurations for agent type filtering
  const [{ data: agentConfigurations, fetching: agentConfigsFetching }] = useFindMany(
    api.agentConfiguration,
    {
      select: {
        id: true,
        name: true,
        description: true,
        isActive: true
      },
      filter: { isActive: { equals: true } },
      sort: { name: 'Ascending' }
    }
  );

  // Build agent message filter
  const buildAgentMessageFilter = () => {
    const filter = {};
    
    if (filters.qualityRating) {
      filter.qualityRating = { equals: parseInt(filters.qualityRating) };
    }
    
    if (filters.trainingFlag !== '') {
      filter.flaggedForTraining = { equals: filters.trainingFlag === 'true' };
    }
    
    if (filters.dateFrom) {
      filter.createdAt = { ...filter.createdAt, greaterThanOrEqual: filters.dateFrom };
    }
    
    if (filters.dateTo) {
      filter.createdAt = { ...filter.createdAt, lessThanOrEqual: filters.dateTo };
    }

    if (filters.agentType) {
      filter.agentType = { equals: filters.agentType };
    }

    console.log('Agent message filter:', JSON.stringify(filter, null, 2));
    return filter;
  };

  // Fetch agent messages
  const [{ data: agentMessages, fetching: agentMessagesFetching, error: agentMessagesError }] = useFindMany(
    api.agentMessage,
    {
      filter: filters.dataSource === 'legacy' ? {} : buildAgentMessageFilter(),
      search: filters.search && filters.dataSource !== 'legacy' ? filters.search : undefined,
      sort: { createdAt: 'Descending' },
      skip: filters.dataSource === 'legacy',
      select: {
        id: true,
        createdAt: true,
        content: true,
        messageType: true,
        qualityRating: true,
        agentType: true,
        flaggedForTraining: true,
        userFlagged: true,
        userFlagReason: true,
        reviewedAt: true,
        reviewedBy: { id: true, email: true },
        conversation: { 
          id: true, 
          conversationTitle: true,
          user: { id: true, email: true, firstName: true, lastName: true },
          agentConfiguration: { id: true, name: true }
        }
      }
    }
  );

  // Fetch all messages for analytics
  const [{ data: allMessages, fetching: allMessagesFetching }] = useFindMany(
    api.message,
    {
      select: {
        id: true,
        reviewStatus: true,
        qualityRating: true,
        trainingFlag: true,
        contentType: true
      }
    }
  );

  // Fetch all agent messages for analytics
  const [{ data: allAgentMessages, fetching: allAgentMessagesFetching }] = useFindMany(
    api.agentMessage,
    {
      select: {
        id: true,
        qualityRating: true,
        flaggedForTraining: true,
        userFlagged: true,
        messageType: true,
        agentType: true
      }
    }
  );

  // Fetch training data for analytics
  const [{ data: trainingData, fetching: trainingDataFetching }] = useFindMany(
    api.aiTrainingData,
    {
      select: {
        id: true,
        jobStatus: true,
        trainingCategory: true,
        qualityScore: true,
        createdAt: true,
        trainingCompletedAt: true,
        assistantUsed: { id: true, name: true }
      },
      sort: { createdAt: 'Descending' }
    }
  );

  // Actions
  const [{ fetching: updateFetching }, updateMessage] = useAction(api.message.update);
  const [{ data: exportData, fetching: exportFetching, error: exportError }, exportTrainingData] = useGlobalAction(api.exportOpenAIFineTuningData);
  const [{ data: batchData, fetching: batchFetching, error: batchError }, processBatch] = useGlobalAction(api.processPendingFineTuningBatch);
  const [{ data: monitorData, fetching: monitorFetching, error: monitorError }, monitorJobs] = useGlobalAction(api.monitorFineTuningJobs);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    console.log(`Filter changed - ${key}:`, value);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle export option changes
  const handleExportOptionChange = (key, value) => {
    setExportOptions(prev => ({ ...prev, [key]: value }));
  };

  // Handle message selection
  const handleMessageSelect = (messageId, isSelected) => {
    const newSelected = new Set(selectedMessages);
    if (isSelected) {
      newSelected.add(messageId);
    } else {
      newSelected.delete(messageId);
    }
    setSelectedMessages(newSelected);
    setSelectAll(newSelected.size === messages?.length);
  };

  // Combine and filter messages for display
  const combinedMessages = React.useMemo(() => {
    const legacyMessages = (messages || []).map(msg => ({ ...msg, isAgentMessage: false }));
    const agentMsgs = (agentMessages || []).map(msg => ({ ...msg, isAgentMessage: true }));
    
    const combined = [...legacyMessages, ...agentMsgs].sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    return combined;
  }, [messages, agentMessages]);

  // Loading state for combined data - only consider fetching if not skipped
  const combinedFetching = (filters.dataSource !== 'agent' && messagesFetching) || 
                          (filters.dataSource !== 'legacy' && agentMessagesFetching);

  // Debug effect for data source changes
  React.useEffect(() => {
    console.log('=== DATA SOURCE DEBUG ===');
    console.log('Current dataSource filter:', filters.dataSource);
    console.log('Legacy messages query skipped:', filters.dataSource === 'agent');
    console.log('Agent messages query skipped:', filters.dataSource === 'legacy');
    console.log('Legacy messages count:', messages?.length || 0);
    console.log('Agent messages count:', agentMessages?.length || 0);
    console.log('Combined messages count:', combinedMessages?.length || 0);
    console.log('Legacy messages fetching:', messagesFetching);
    console.log('Agent messages fetching:', agentMessagesFetching);
    console.log('Combined fetching:', combinedFetching);
    console.log('========================');
  }, [filters.dataSource, messages?.length, agentMessages?.length, combinedMessages?.length, messagesFetching, agentMessagesFetching, combinedFetching]);

  // Debug effect for filter changes
  React.useEffect(() => {
    console.log('=== FILTER DEBUG ===');
    console.log('Current filters:', filters);
    console.log('Legacy filter object:', filters.dataSource !== 'agent' ? buildLegacyFilter() : 'SKIPPED');
    console.log('Agent filter object:', filters.dataSource !== 'legacy' ? buildAgentMessageFilter() : 'SKIPPED');
    console.log('===================');
  }, [filters]);

  // Debug effect for combined messages
  React.useEffect(() => {
    console.log('=== COMBINED MESSAGES DEBUG ===');
    console.log('Legacy messages:', messages?.map(m => ({ id: m.id, isAgent: false, type: 'legacy' })) || []);
    console.log('Agent messages:', agentMessages?.map(m => ({ id: m.id, isAgent: true, type: 'agent' })) || []);
    console.log('Combined messages:', combinedMessages?.map(m => ({ id: m.id, isAgent: m.isAgentMessage })) || []);
    console.log('==============================');
  }, [messages, agentMessages, combinedMessages]);

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      const allMessageIds = combinedMessages.map(m => m.id);
      setSelectedMessages(new Set(allMessageIds));
    } else {
      setSelectedMessages(new Set());
    }
    setSelectAll(isSelected);
  };

  // Quick actions
  const handleQuickAction = async (messageId, action) => {
    if (!session?.id) {
      toast({
        title: 'Error',
        description: 'Session not available. Please refresh and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      let updateData = {};
      
      switch (action) {
        case 'approve':
          updateData = { 
            reviewStatus: 'approved',
            reviewedAt: new Date().toISOString(),
            reviewedBy: { _link: session.id }
          };
          break;
        case 'flag':
          updateData = { 
            reviewStatus: 'flagged',
            reviewedAt: new Date().toISOString(),
            reviewedBy: { _link: session.id }
          };
          break;
        case 'needReview':
          updateData = { 
            reviewStatus: 'pending',
            reviewedAt: new Date().toISOString(),
            reviewedBy: { _link: session.id }
          };
          break;
        default:
          return;
      }

      await updateMessage({ id: messageId, ...updateData });
      
      toast({
        title: 'Message updated',
        description: `Message ${action} successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Quick action error:', error);
      toast({
        title: 'Error',
        description: 'Failed to update message',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Bulk actions
  const handleBulkAction = async (action) => {
    if (selectedMessages.size === 0) {
      toast({
        title: 'No messages selected',
        description: 'Please select messages to perform bulk action',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!session?.id && (action === 'approve' || action === 'flag')) {
      toast({
        title: 'Error',
        description: 'Session not available. Please refresh and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const promises = Array.from(selectedMessages).map(messageId => {
        let updateData = {};
        
        switch (action) {
          case 'approve':
            updateData = { 
              reviewStatus: 'approved',
              reviewedAt: new Date().toISOString(),
              reviewedBy: { _link: session.id }
            };
            break;
          case 'flag':
            updateData = { 
              reviewStatus: 'flagged',
              reviewedAt: new Date().toISOString(),
              reviewedBy: { _link: session.id }
            };
            break;
          case 'export':
            // For export, we don't update the message, just mark as training ready
            updateData = { trainingFlag: true };
            break;
          default:
            return Promise.resolve();
        }

        return updateMessage({ id: messageId, ...updateData });
      });

      await Promise.all(promises);
      
      toast({
        title: 'Bulk action completed',
        description: `${selectedMessages.size} messages updated successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setSelectedMessages(new Set());
      setSelectAll(false);
    } catch (error) {
      console.error('Bulk action error:', error);
      toast({
        title: 'Error',
        description: 'Failed to perform bulk action',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Open quality assessment modal
  const openQualityAssessment = (message) => {
    setSelectedMessage(message);
    setQualityRating(message.qualityRating || 0);
    setAdminFeedback(message.adminFeedback?.markdown || '');
    setContentType(message.contentType || '');
    setTrainingFlag(message.trainingFlag ?? true);
    onQualityOpen();
  };

  // Save quality assessment
  const saveQualityAssessment = async () => {
    if (!session?.id) {
      toast({
        title: 'Error',
        description: 'Session not available. Please refresh and try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updateData = {
        id: selectedMessage.id,
        qualityRating,
        contentType,
        trainingFlag,
        reviewStatus: 'reviewed',
        reviewedAt: new Date().toISOString(),
        reviewedBy: { _link: session.id }
      };

      // Only include adminFeedback if it has content
      if (adminFeedback && adminFeedback.trim()) {
        updateData.adminFeedback = { markdown: adminFeedback };
      }

      await updateMessage(updateData);
      
      toast({
        title: 'Assessment saved',
        description: 'Quality assessment saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onQualityClose();
    } catch (error) {
      console.error('Save assessment error:', error);
      toast({
        title: 'Error',
        description: 'Failed to save assessment',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      const exportParams = {
        exportType: exportOptions.exportType,
        reverseQuestionAnswer: exportOptions.reverseQuestionAnswer,
        reviewStatus: exportOptions.reviewStatus || 'approved'
      };

      // Add optional parameters only if they have values
      if (exportOptions.minQualityScore !== null && exportOptions.minQualityScore !== '') {
        exportParams.minQualityScore = parseInt(exportOptions.minQualityScore);
      }
      if (exportOptions.maxQualityScore !== null && exportOptions.maxQualityScore !== '') {
        exportParams.maxQualityScore = parseInt(exportOptions.maxQualityScore);
      }
      if (exportOptions.dateFrom) {
        exportParams.dateFrom = exportOptions.dateFrom;
      }
      if (exportOptions.dateTo) {
        exportParams.dateTo = exportOptions.dateTo;
      }
      if (exportOptions.assistantId) {
        exportParams.assistantId = exportOptions.assistantId;
      }

      await exportTrainingData(exportParams);
    } catch (error) {
      toast({
        title: 'Export Error',
        description: 'Failed to export training data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Handle successful export
  React.useEffect(() => {
    if (exportData && exportData.success && exportData.result) {
      try {
        // Create and download the file
        const jsonlContent = exportData.result.content || exportData.result;
        const blob = new Blob([jsonlContent], { type: 'application/jsonl' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `fine-tuning-data-${new Date().toISOString().split('T')[0]}.jsonl`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast({
          title: 'Export Complete',
          description: `Training data exported successfully! ${exportData.result.count || 'Multiple'} messages exported.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        onExportClose();
      } catch (error) {
        toast({
          title: 'Download Error',
          description: 'Export completed but failed to download file',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } else if (exportData && !exportData.success) {
      toast({
        title: 'Export Failed',
        description: exportData.errors?.[0]?.message || 'Export failed for unknown reason',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [exportData, toast, onExportClose]);

  // Handle export error
  React.useEffect(() => {
    if (exportError) {
      toast({
        title: 'Export Error',
        description: exportError.toString(),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [exportError, toast]);

  // Handle fine-tuning batch processing
  const handleProcessBatch = async () => {
    try {
      setFineTuningState(prev => ({ ...prev, batchProcessing: true }));
      await processBatch();
    } catch (error) {
      toast({
        title: 'Batch Processing Error',
        description: 'Failed to process training batch',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFineTuningState(prev => ({ ...prev, batchProcessing: false }));
    }
  };

  // Handle job monitoring
  const handleMonitorJobs = async () => {
    try {
      setFineTuningState(prev => ({ ...prev, jobMonitoring: true }));
      await monitorJobs();
    } catch (error) {
      toast({
        title: 'Job Monitoring Error',
        description: 'Failed to monitor fine-tuning jobs',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setFineTuningState(prev => ({ ...prev, jobMonitoring: false }));
    }
  };

  // Handle batch processing results
  React.useEffect(() => {
    if (batchData) {
      setFineTuningState(prev => ({ ...prev, lastBatchResult: batchData }));
      if (batchData.success) {
        toast({
          title: 'Batch Processing Complete',
          description: `Successfully processed training batch. ${batchData.result?.processed || 0} records processed.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Batch Processing Failed',
          description: batchData.errors?.[0]?.message || 'Unknown error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [batchData, toast]);

  // Handle job monitoring results
  React.useEffect(() => {
    if (monitorData) {
      setFineTuningState(prev => ({ ...prev, lastJobMonitorResult: monitorData }));
      if (monitorData.success) {
        toast({
          title: 'Job Monitoring Complete',
          description: `Monitoring complete. ${monitorData.result?.jobsChecked || 0} jobs checked.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Job Monitoring Failed',
          description: monitorData.errors?.[0]?.message || 'Unknown error occurred',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [monitorData, toast]);

  // Handle batch and monitor errors
  React.useEffect(() => {
    if (batchError) {
      toast({
        title: 'Batch Processing Error',
        description: batchError.toString(),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [batchError, toast]);

  React.useEffect(() => {
    if (monitorError) {
      toast({
        title: 'Job Monitoring Error',
        description: monitorError.toString(),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [monitorError, toast]);

  // Calculate analytics
  const analytics = React.useMemo(() => {
    if (!allMessages && !allAgentMessages) return null;

    // Legacy message analytics
    const legacyTotal = allMessages?.length || 0;
    const legacyReviewed = allMessages?.filter(m => m.reviewStatus === 'reviewed' || m.reviewStatus === 'approved').length || 0;
    const legacyFlagged = allMessages?.filter(m => m.reviewStatus === 'flagged').length || 0;
    const legacyPending = allMessages?.filter(m => m.reviewStatus === 'pending').length || 0;
    const legacyTrainingReady = allMessages?.filter(m => m.trainingFlag && m.reviewStatus === 'approved').length || 0;
    
    const legacyQualityRatings = allMessages?.filter(m => m.qualityRating).map(m => m.qualityRating) || [];
    const legacyAvgQuality = legacyQualityRatings.length > 0 ? 
      legacyQualityRatings.reduce((a, b) => a + b, 0) / legacyQualityRatings.length : 0;

    // Agent message analytics
    const agentTotal = allAgentMessages?.length || 0;
    const agentFlagged = allAgentMessages?.filter(m => m.userFlagged).length || 0;
    const agentTrainingReady = allAgentMessages?.filter(m => m.flaggedForTraining).length || 0;
    
    const agentQualityRatings = allAgentMessages?.filter(m => m.qualityRating).map(m => m.qualityRating) || [];
    const agentAvgQuality = agentQualityRatings.length > 0 ? 
      agentQualityRatings.reduce((a, b) => a + b, 0) / agentQualityRatings.length : 0;

    // Combined analytics
    const total = legacyTotal + agentTotal;
    const reviewed = legacyReviewed; // Agent messages don't have review status in same way
    const flagged = legacyFlagged + agentFlagged;
    const pending = legacyPending; // Agent messages don't have pending status
    const trainingReady = legacyTrainingReady + agentTrainingReady;
    
    const allQualityRatings = [...legacyQualityRatings, ...agentQualityRatings];
    const avgQuality = allQualityRatings.length > 0 ? 
      allQualityRatings.reduce((a, b) => a + b, 0) / allQualityRatings.length : 0;

    // Training data analytics
    const trainingStats = trainingData ? {
      totalTrainingRecords: trainingData.length,
      completedJobs: trainingData.filter(t => t.jobStatus === 'completed').length,
      pendingJobs: trainingData.filter(t => t.jobStatus === 'pending_batch' || t.jobStatus === 'queued_for_upload').length,
      failedJobs: trainingData.filter(t => t.jobStatus === 'failed').length,
      avgTrainingQuality: trainingData.filter(t => t.qualityScore).length > 0 ? 
        trainingData.filter(t => t.qualityScore).reduce((a, b) => a + b.qualityScore, 0) / trainingData.filter(t => t.qualityScore).length : 0
    } : {
      totalTrainingRecords: 0,
      completedJobs: 0,
      pendingJobs: 0,
      failedJobs: 0,
      avgTrainingQuality: 0
    };

    return {
      total,
      reviewed,
      flagged,
      pending,
      trainingReady,
      avgQuality: avgQuality.toFixed(1),
      completionRate: total > 0 ? ((reviewed / total) * 100).toFixed(1) : 0,
      legacyTotal,
      agentTotal,
      legacyTrainingReady,
      agentTrainingReady,
      ...trainingStats
    };
  }, [allMessages, allAgentMessages, trainingData]);

  // Render star rating
  const renderStarRating = (rating, onRatingChange = null, hasFeedback = false) => {
    const activeColor = useColorModeValue('yellow.400', 'yellow.300');
    const inactiveColor = useColorModeValue('gray.300', 'gray.600');
    const feedbackColor = useColorModeValue('orange.500', 'orange.300');
    
    return (
      <HStack spacing={1}>
        {[1, 2, 3, 4, 5].map(star => (
          <IconButton
            key={star}
            aria-label={`${star} stars`}
            icon={<StarIcon />}
            size="sm"
            variant="ghost"
            color={star <= rating ? activeColor : inactiveColor}
            onClick={() => onRatingChange && onRatingChange(star)}
            cursor={onRatingChange ? 'pointer' : 'default'}
          />
        ))}
        {hasFeedback && (
          <IconButton
            aria-label="Has feedback"
            icon={<ChatIcon />}
            size="sm"
            variant="ghost"
            color={feedbackColor}
            cursor="default"
          />
        )}
      </HStack>
    );
  };

  // Render message content for both legacy and agent messages
  const renderMessageContent = (message, isAgentMessage = false) => {
    const userColor = useColorModeValue('blue.600', 'blue.300');
    const assistantColor = useColorModeValue('green.600', 'green.300');
    const feedbackColor = useColorModeValue('orange.600', 'orange.300');
    const textColor = useColorModeValue('gray.800', 'gray.200');
    const agentTypeColor = useColorModeValue('purple.600', 'purple.300');
    
    if (isAgentMessage) {
      // For agent messages, content structure is different
      const content = message.content;
      let userMsg = '';
      let assistantMsg = '';
      
      if (typeof content === 'object') {
        userMsg = content.user_message || content.input || JSON.stringify(content);
        assistantMsg = content.assistant_message || content.output || content.response || '';
      } else {
        assistantMsg = content;
      }
      
      return (
        <VStack align="stretch" spacing={2}>
          <HStack>
            <Badge colorScheme="purple" size="sm">Agent Message</Badge>
            {message.agentType && (
              <Badge colorScheme="blue" size="sm">{message.agentType.toUpperCase()}</Badge>
            )}
            <Badge colorScheme={message.messageType === 'user_input' ? 'blue' : 'green'} size="sm">
              {message.messageType}
            </Badge>
          </HStack>
          {userMsg && (
            <Box>
              <Text fontSize="sm" fontWeight="bold" color={userColor}>
                User:
              </Text>
              <Text fontSize="sm" mt={1} color={textColor}>
                {userMsg}
              </Text>
            </Box>
          )}
          {assistantMsg && (
            <Box>
              <Text fontSize="sm" fontWeight="bold" color={assistantColor}>
                Assistant:
              </Text>
              <Text fontSize="sm" mt={1} color={textColor}>
                {assistantMsg}
              </Text>
            </Box>
          )}
        </VStack>
      );
    } else {
      // For legacy messages
      const userMsg = typeof message.userMessage === 'string' 
        ? message.userMessage 
        : JSON.stringify(message.userMessage);
      const assistantMsg = typeof message.assistantMessage === 'string' 
        ? message.assistantMessage 
        : JSON.stringify(message.assistantMessage);
      
      return (
        <VStack align="stretch" spacing={2}>
          <HStack>
            <Badge colorScheme="blue" size="sm">Legacy Message</Badge>
            {message.contentType && (
              <Badge colorScheme="gray" size="sm">{message.contentType}</Badge>
            )}
          </HStack>
          <Box>
            <Text fontSize="sm" fontWeight="bold" color={userColor}>
              User:
            </Text>
            <Text fontSize="sm" mt={1} color={textColor}>
              {userMsg}
            </Text>
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="bold" color={assistantColor}>
              Assistant:
            </Text>
            <Text fontSize="sm" mt={1} color={textColor}>
              {assistantMsg}
            </Text>
          </Box>
        </VStack>
      );
    }
  };

  // Handle error states
  if (messagesError) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading messages</AlertTitle>
          <AlertDescription>{messagesError.toString()}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  if (agentMessagesError) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading agent messages</AlertTitle>
          <AlertDescription>{agentMessagesError.toString()}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={6} maxW="full" mx="auto">
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex align="center" justify="space-between">
          <HStack spacing={4}>
            <IconButton
              aria-label="Back to Admin"
              icon={<ArrowBackIcon />}
              variant="ghost"
              size="md"
              onClick={() => navigate('/admin')}
            />
            <Heading size="lg">Fine-Tuning Dashboard</Heading>
          </HStack>
          <ButtonGroup>
            <Button 
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              onClick={onExportOpen}
            >
              Export Training Data
            </Button>
          </ButtonGroup>
        </Flex>

        {/* Analytics Dashboard */}
        {analytics && (
          <Card>
            <CardHeader>
              <Heading size="md">Analytics Overview</Heading>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={6}>
                <Stat>
                  <StatLabel>Total Messages</StatLabel>
                  <StatNumber>{analytics.total}</StatNumber>
                  <StatHelpText>Legacy: {analytics.legacyTotal}, Agent: {analytics.agentTotal}</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Reviewed</StatLabel>
                  <StatNumber>{analytics.reviewed}</StatNumber>
                  <StatHelpText>{analytics.completionRate}% complete</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Pending</StatLabel>
                  <StatNumber>{analytics.pending}</StatNumber>
                  <StatHelpText>Legacy only</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Flagged</StatLabel>
                  <StatNumber>{analytics.flagged}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Training Ready</StatLabel>
                  <StatNumber>{analytics.trainingReady}</StatNumber>
                  <StatHelpText>Legacy: {analytics.legacyTrainingReady}, Agent: {analytics.agentTrainingReady}</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel>Avg Quality</StatLabel>
                  <StatNumber>{analytics.avgQuality}</StatNumber>
                  <StatHelpText>out of 5.0</StatHelpText>
                </Stat>
              </SimpleGrid>
              
              <Divider my={4} />
              
              <Text fontSize="md" fontWeight="semibold" mb={4}>Training Data Status</Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                <Stat>
                  <StatLabel>Total Training Records</StatLabel>
                  <StatNumber>{analytics.totalTrainingRecords}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Completed Jobs</StatLabel>
                  <StatNumber>{analytics.completedJobs}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Pending Jobs</StatLabel>
                  <StatNumber>{analytics.pendingJobs}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Failed Jobs</StatLabel>
                  <StatNumber>{analytics.failedJobs}</StatNumber>
                </Stat>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* Fine-Tuning Job Management */}
        <Card>
          <CardHeader>
            <Heading size="md">Fine-Tuning Job Management</Heading>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text color={useColorModeValue('gray.600', 'gray.400')}>
                Create and monitor fine-tuning jobs using approved training data.
              </Text>
              
              <HStack spacing={4}>
                <Button
                  leftIcon={<RepeatIcon />}
                  colorScheme="blue"
                  onClick={handleProcessBatch}
                  isLoading={fineTuningState.batchProcessing || batchFetching}
                  loadingText="Processing..."
                >
                  Create Training Batch
                </Button>
                
                <Button
                  leftIcon={<ViewIcon />}
                  colorScheme="green"
                  onClick={handleMonitorJobs}
                  isLoading={fineTuningState.jobMonitoring || monitorFetching}
                  loadingText="Monitoring..."
                >
                  Monitor Jobs
                </Button>
              </HStack>

              {fineTuningState.lastBatchResult && (
                <Alert status={fineTuningState.lastBatchResult.success ? 'success' : 'error'}>
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <AlertTitle>
                      Last Batch: {fineTuningState.lastBatchResult.success ? 'Success' : 'Failed'}
                    </AlertTitle>
                    <AlertDescription>
                      {fineTuningState.lastBatchResult.result?.message || 
                       fineTuningState.lastBatchResult.errors?.[0]?.message ||
                       'No additional details available'}
                    </AlertDescription>
                  </VStack>
                </Alert>
              )}

              {fineTuningState.lastJobMonitorResult && (
                <Alert status={fineTuningState.lastJobMonitorResult.success ? 'success' : 'error'}>
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <AlertTitle>
                      Last Monitor: {fineTuningState.lastJobMonitorResult.success ? 'Success' : 'Failed'}
                    </AlertTitle>
                    <AlertDescription>
                      {fineTuningState.lastJobMonitorResult.result?.message || 
                       fineTuningState.lastJobMonitorResult.errors?.[0]?.message ||
                       'No additional details available'}
                    </AlertDescription>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader pb={2}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Filters</Heading>
              <HStack spacing={2}>
                {combinedFetching && <Spinner size="sm" />}
                <Badge colorScheme="blue" fontSize="xs">
                  {combinedMessages?.length || 0} results
                </Badge>
              </HStack>
            </Flex>
            {/* Debug info */}
            <VStack spacing={1} align="start" mt={2}>
              <Text fontSize="xs" color={useColorModeValue('gray.500', 'gray.400')}>
                Debug: Legacy: {messages?.length || 0} | Agent: {agentMessages?.length || 0} | 
                Data Source: {filters.dataSource} | 
                Legacy Fetching: {messagesFetching ? 'Yes' : 'No'} | 
                Agent Fetching: {agentMessagesFetching ? 'Yes' : 'No'}
              </Text>
              {filters.dataSource !== 'both' && (
                <Badge colorScheme="yellow" fontSize="xs">
                  Filtering by: {filters.dataSource === 'legacy' ? 'Legacy Messages Only' : 'Agent Messages Only'}
                </Badge>
              )}
            </VStack>
          </CardHeader>
          <CardBody p={4}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm">Review Status</FormLabel>
                <Select 
                  size="sm"
                  value={filters.reviewStatus} 
                  onChange={(e) => handleFilterChange('reviewStatus', e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="approved">Approved</option>
                  <option value="flagged">Flagged</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Quality Rating</FormLabel>
                <Select 
                  size="sm"
                  value={filters.qualityRating} 
                  onChange={(e) => handleFilterChange('qualityRating', e.target.value)}
                >
                  <option value="">All Ratings</option>
                  <option value="1">1 Star</option>
                  <option value="2">2 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="5">5 Stars</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Content Type</FormLabel>
                <Select 
                  size="sm"
                  value={filters.contentType} 
                  onChange={(e) => handleFilterChange('contentType', e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="user_question">User Question</option>
                  <option value="assistant_response">Assistant Response</option>
                  <option value="system_message">System Message</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Training Flag</FormLabel>
                <Select 
                  size="sm"
                  value={filters.trainingFlag} 
                  onChange={(e) => handleFilterChange('trainingFlag', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Training Ready</option>
                  <option value="false">Not Training Ready</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Date From</FormLabel>
                <Input 
                  size="sm"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Date To</FormLabel>
                <Input 
                  size="sm"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Data Source</FormLabel>
                <Select 
                  size="sm"
                  value={filters.dataSource} 
                  onChange={(e) => handleFilterChange('dataSource', e.target.value)}
                >
                  <option value="both">Both Legacy & Agent</option>
                  <option value="legacy">Legacy Only</option>
                  <option value="agent">Agent Only</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Assistant (Legacy)</FormLabel>
                <Select 
                  size="sm"
                  value={filters.assistantId} 
                  onChange={(e) => handleFilterChange('assistantId', e.target.value)}
                  placeholder={assistantsFetching ? "Loading..." : "All Assistants"}
                  isDisabled={assistantsFetching || filters.dataSource === 'agent'}
                >
                  {assistants?.map((assistant) => (
                    <option key={assistant.id} value={assistant.id}>
                      {assistant.name || `Assistant ${assistant.id}`}
                      {assistant.assistantType === 'document_processor' && ' (Doc)'}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Agent Type</FormLabel>
                <Select 
                  size="sm"
                  value={filters.agentType} 
                  onChange={(e) => handleFilterChange('agentType', e.target.value)}
                  placeholder={agentConfigsFetching ? "Loading..." : "All Agent Types"}
                  isDisabled={agentConfigsFetching || filters.dataSource === 'legacy'}
                >
                  <option value="don">Don</option>
                  <option value="maia">Maia</option>
                  <option value="gerald">Gerald</option>
                  <option value="custom">Custom</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Search</FormLabel>
                <Input 
                  size="sm"
                  placeholder="Search messages..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Actions</FormLabel>
                <Button 
                  size="sm"
                  onClick={() => setFilters({
                    reviewStatus: '',
                    qualityRating: '',
                    contentType: '',
                    trainingFlag: '',
                    dateFrom: '',
                    dateTo: '',
                    search: '',
                    assistantId: '',
                    dataSource: 'both',
                    agentType: ''
                  })}
                >
                  Clear Filters
                </Button>
              </FormControl>
            </SimpleGrid>
          </CardBody>
        </Card>

        {/* Bulk Actions */}
        <Card>
          <CardBody p={4}>
            <Flex align="center" gap={4}>
              <Text fontSize="sm">
                {selectedMessages.size} of {combinedMessages?.length || 0} messages selected
              </Text>
              <ButtonGroup>
                <Button
                  size="sm"
                  leftIcon={<CheckIcon />}
                  colorScheme="green"
                  onClick={() => handleBulkAction('approve')}
                  isDisabled={selectedMessages.size === 0}
                >
                  Bulk Approve
                </Button>
                <Button
                  size="sm"
                  leftIcon={<WarningIcon />}
                  colorScheme="red"
                  onClick={() => handleBulkAction('flag')}
                  isDisabled={selectedMessages.size === 0}
                >
                  Bulk Flag
                </Button>
                <Button
                  size="sm"
                  leftIcon={<DownloadIcon />}
                  colorScheme="blue"
                  onClick={() => handleBulkAction('export')}
                  isDisabled={selectedMessages.size === 0}
                >
                  Export Selected
                </Button>
              </ButtonGroup>
            </Flex>
          </CardBody>
        </Card>

        {/* Message Review Queue */}
        <Card>
          <CardHeader>
            <Heading size="md">Message Review Queue</Heading>
          </CardHeader>
          <CardBody>
            {combinedFetching ? (
              <Flex justify="center" p={8}>
                <VStack spacing={4}>
                  <Spinner size="lg" />
                  <Text color={useColorModeValue('gray.600', 'gray.400')}>
                    Loading {filters.dataSource === 'both' ? 'legacy and agent' : filters.dataSource} messages...
                  </Text>
                </VStack>
              </Flex>
            ) : combinedMessages && combinedMessages.length > 0 ? (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th py={2}>
                        <Checkbox
                          isChecked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </Th>
                      <Th py={2}>Message</Th>
                      <Th py={2}>Status</Th>
                      <Th py={2}>Quality</Th>
                      <Th py={2}>Feedback</Th>
                      <Th py={2}>User</Th>
                      <Th py={2}>Date</Th>
                      <Th py={2}>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {combinedMessages?.map((message) => (
                      <Tr key={`${message.isAgentMessage ? 'agent' : 'legacy'}-${message.id}`}>
                        <Td py={2}>
                          <Checkbox
                            isChecked={selectedMessages.has(message.id)}
                            onChange={(e) => handleMessageSelect(message.id, e.target.checked)}
                          />
                        </Td>
                        <Td maxW="400px" py={2}>
                          <Box>
                            {renderMessageContent(message, message.isAgentMessage)}
                          </Box>
                        </Td>
                        <Td py={2}>
                          {message.isAgentMessage ? (
                            <VStack spacing={1} align="start">
                              <Badge colorScheme={message.userFlagged ? 'red' : 'gray'}>
                                {message.userFlagged ? 'User Flagged' : 'No Issues'}
                              </Badge>
                              {message.flaggedForTraining && (
                                <Badge colorScheme="green" size="sm">Training Ready</Badge>
                              )}
                            </VStack>
                          ) : (
                            <Badge 
                              colorScheme={
                                message.reviewStatus === 'approved' ? 'green' :
                                message.reviewStatus === 'flagged' ? 'red' :
                                message.reviewStatus === 'reviewed' ? 'blue' : 'gray'
                              }
                            >
                              {message.reviewStatus || 'pending'}
                            </Badge>
                          )}
                        </Td>
                        <Td py={2}>
                          {message.qualityRating ? (
                            renderStarRating(
                              message.qualityRating, 
                              null, 
                              !!(message.adminFeedback?.markdown || message.userFlagReason)
                            )
                          ) : (
                            <HStack>
                              <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')}>Not rated</Text>
                              {(message.adminFeedback?.markdown || message.userFlagReason) && (
                                <IconButton
                                  aria-label="Has feedback"
                                  icon={<ChatIcon />}
                                  size="sm"
                                  variant="ghost"
                                  color={useColorModeValue('orange.500', 'orange.300')}
                                  cursor="default"
                                />
                              )}
                            </HStack>
                          )}
                        </Td>
                        <Td maxW="200px" py={2}>
                          {message.isAgentMessage ? (
                            message.userFlagReason ? (
                              <Text fontSize="sm" color={useColorModeValue('gray.700', 'gray.300')} noOfLines={2}>
                                {message.userFlagReason}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')} fontStyle="italic">
                                No user feedback
                              </Text>
                            )
                          ) : (
                            message.adminFeedback?.markdown ? (
                              <Text fontSize="sm" color={useColorModeValue('gray.700', 'gray.300')} noOfLines={2}>
                                {message.adminFeedback.markdown}
                              </Text>
                            ) : (
                              <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')} fontStyle="italic">
                                No admin feedback
                              </Text>
                            )
                          )}
                        </Td>
                        <Td py={2}>
                          <Text fontSize="sm">
                            {message.isAgentMessage ? 
                              (message.conversation?.user?.firstName || message.conversation?.user?.email || 'Unknown') :
                              (message.user?.firstName || message.user?.email || 'Unknown')
                            }
                          </Text>
                        </Td>
                        <Td py={2}>
                          <Text fontSize="sm">
                            {new Date(message.createdAt).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td py={2}>
                          {message.isAgentMessage ? (
                            <ButtonGroup size="sm" spacing={1}>
                              <Button
                                colorScheme="blue"
                                size="xs"
                                isDisabled={true}
                              >
                                Agent Msg
                              </Button>
                              <Text fontSize="xs" color={useColorModeValue('gray.500', 'gray.400')}>
                                Limited actions
                              </Text>
                            </ButtonGroup>
                          ) : (
                            <ButtonGroup size="sm" spacing={1}>
                              <Button
                                colorScheme="green"
                                onClick={() => handleQuickAction(message.id, 'approve')}
                                isLoading={updateFetching}
                                size="xs"
                              >
                                Approve
                              </Button>
                              <Button
                                colorScheme="red"
                                onClick={() => handleQuickAction(message.id, 'flag')}
                                isLoading={updateFetching}
                                size="xs"
                              >
                                Flag
                              </Button>
                              <Button
                                colorScheme="blue"
                                onClick={() => openQualityAssessment(message)}
                                leftIcon={message.adminFeedback?.markdown || message.qualityRating ? <ChatIcon /> : undefined}
                                size="xs"
                              >
                                {message.adminFeedback?.markdown || message.qualityRating ? 'Edit Assessment' : 'Assess'}
                              </Button>
                            </ButtonGroup>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              <VStack spacing={4} py={8}>
                <Text color={useColorModeValue('gray.500', 'gray.400')} textAlign="center" fontSize="lg">
                  No messages found
                </Text>
                <VStack spacing={2}>
                  <Text color={useColorModeValue('gray.400', 'gray.500')} textAlign="center" fontSize="sm">
                    {filters.dataSource === 'legacy' && 'No legacy messages match your current filters'}
                    {filters.dataSource === 'agent' && 'No agent messages match your current filters'}
                    {filters.dataSource === 'both' && 'No messages match your current filters'}
                  </Text>
                  <Text color={useColorModeValue('gray.400', 'gray.500')} textAlign="center" fontSize="xs">
                    Debug: Legacy: {messages?.length || 0}, Agent: {agentMessages?.length || 0}, Source: {filters.dataSource}
                  </Text>
                  {(filters.search || filters.reviewStatus || filters.qualityRating || filters.contentType || 
                    filters.trainingFlag || filters.dateFrom || filters.dateTo || filters.assistantId || filters.agentType) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setFilters({
                        reviewStatus: '',
                        qualityRating: '',
                        contentType: '',
                        trainingFlag: '',
                        dateFrom: '',
                        dateTo: '',
                        search: '',
                        assistantId: '',
                        dataSource: filters.dataSource, // Keep current data source
                        agentType: ''
                      })}
                    >
                      Clear Filters (Keep Data Source: {filters.dataSource})
                    </Button>
                  )}
                </VStack>
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* Training Data Management */}
        <Card>
          <CardHeader>
            <Heading size="md">Training Data Management</Heading>
          </CardHeader>
          <CardBody>
            {trainingDataFetching ? (
              <Flex justify="center" p={8}>
                <Spinner size="lg" />
              </Flex>
            ) : trainingData && trainingData.length > 0 ? (
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th py={2}>Job Status</Th>
                      <Th py={2}>Category</Th>
                      <Th py={2}>Quality Score</Th>
                      <Th py={2}>Assistant</Th>
                      <Th py={2}>Created</Th>
                      <Th py={2}>Completed</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {trainingData.slice(0, 10).map((record) => (
                      <Tr key={record.id}>
                        <Td py={2}>
                          <Badge 
                            colorScheme={
                              record.jobStatus === 'completed' ? 'green' :
                              record.jobStatus === 'failed' ? 'red' :
                              record.jobStatus === 'training' ? 'blue' : 'gray'
                            }
                          >
                            {record.jobStatus}
                          </Badge>
                        </Td>
                        <Td py={2}>
                          <Badge 
                            colorScheme={
                              record.trainingCategory === 'good_example' ? 'green' :
                              record.trainingCategory === 'needs_improvement' ? 'yellow' :
                              record.trainingCategory === 'exclude_from_training' ? 'red' : 'gray'
                            }
                          >
                            {record.trainingCategory}
                          </Badge>
                        </Td>
                        <Td py={2}>
                          {record.qualityScore ? (
                            <HStack spacing={1}>
                              <Text fontSize="sm">{record.qualityScore}/5</Text>
                              {renderStarRating(record.qualityScore)}
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')}>Not rated</Text>
                          )}
                        </Td>
                        <Td py={2}>
                          <Text fontSize="sm">
                            {record.assistantUsed?.name || 'Unknown'}
                          </Text>
                        </Td>
                        <Td py={2}>
                          <Text fontSize="sm">
                            {new Date(record.createdAt).toLocaleDateString()}
                          </Text>
                        </Td>
                        <Td py={2}>
                          <Text fontSize="sm">
                            {record.trainingCompletedAt ? 
                              new Date(record.trainingCompletedAt).toLocaleDateString() : 
                              'Not completed'
                            }
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {trainingData.length > 10 && (
                  <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')} mt={2}>
                    Showing first 10 of {trainingData.length} training records
                  </Text>
                )}
              </TableContainer>
            ) : (
              <Text color={useColorModeValue('gray.500', 'gray.400')} textAlign="center" py={8}>
                No training data records found
              </Text>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Quality Assessment Modal */}
      <Modal isOpen={isQualityOpen} onClose={onQualityClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <ChatIcon color={useColorModeValue('blue.500', 'blue.300')} />
              <Text>
                {selectedMessage?.adminFeedback?.markdown || selectedMessage?.qualityRating ? 
                  'Edit Quality Assessment' : 'Create Quality Assessment'}
              </Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedMessage && (
              <VStack spacing={6} align="stretch">
                {/* Message Content Section */}
                <Box>
                  <Text fontWeight="bold" mb={3} color={useColorModeValue('gray.800', 'gray.200')} fontSize="md">
                    Message Content
                  </Text>
                  <Box 
                    bg={useColorModeValue('gray.50', 'gray.700')} 
                    p={4} 
                    borderRadius="md"
                    border="1px solid"
                    borderColor={useColorModeValue('gray.200', 'gray.600')}
                  >
                    <VStack align="stretch" spacing={3}>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold" color={useColorModeValue('blue.600', 'blue.300')}>
                          User Question:
                        </Text>
                        <Text fontSize="sm" mt={1} color={useColorModeValue('gray.800', 'gray.200')}>
                          {typeof selectedMessage.userMessage === 'string' 
                            ? selectedMessage.userMessage 
                            : JSON.stringify(selectedMessage.userMessage)}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="sm" fontWeight="bold" color={useColorModeValue('green.600', 'green.300')}>
                          AI Response:
                        </Text>
                        <Text fontSize="sm" mt={1} color={useColorModeValue('gray.800', 'gray.200')}>
                          {typeof selectedMessage.assistantMessage === 'string' 
                            ? selectedMessage.assistantMessage 
                            : JSON.stringify(selectedMessage.assistantMessage)}
                        </Text>
                      </Box>
                    </VStack>
                  </Box>
                </Box>

                <Divider />

                {/* Assessment Section */}
                <Box>
                  <Text fontWeight="bold" mb={3} color={useColorModeValue('gray.800', 'gray.200')} fontSize="md">
                    Quality Assessment
                  </Text>
                  <VStack spacing={4} align="stretch">
                    <FormControl>
                      <FormLabel color={useColorModeValue('gray.700', 'gray.200')}>Quality Rating</FormLabel>
                      {renderStarRating(qualityRating, setQualityRating)}
                      <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                        Rate the overall quality of this conversation exchange
                      </Text>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={useColorModeValue('gray.700', 'gray.200')}>Content Type</FormLabel>
                      <Select value={contentType} onChange={(e) => setContentType(e.target.value)}>
                        <option value="">Select type</option>
                        <option value="user_question">User Question</option>
                        <option value="assistant_response">Assistant Response</option>
                        <option value="system_message">System Message</option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel color={useColorModeValue('gray.700', 'gray.200')}>
                        Admin Feedback
                        {selectedMessage.adminFeedback?.markdown && (
                          <Badge ml={2} colorScheme="orange" fontSize="xs">
                            Editing existing feedback
                          </Badge>
                        )}
                      </FormLabel>
                      <Textarea
                        value={adminFeedback}
                        onChange={(e) => setAdminFeedback(e.target.value)}
                        placeholder="Enter detailed feedback about the quality, accuracy, helpfulness, or areas for improvement..."
                        minH="120px"
                        color={useColorModeValue('gray.800', 'gray.100')}
                        _placeholder={{ color: useColorModeValue('gray.500', 'gray.400') }}
                      />
                      <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                        This feedback will be used to improve training data quality
                      </Text>
                    </FormControl>

                    <FormControl>
                      <HStack align="center">
                        <Switch
                          isChecked={trainingFlag}
                          onChange={(e) => setTrainingFlag(e.target.checked)}
                          colorScheme="green"
                        />
                        <FormLabel color={useColorModeValue('gray.700', 'gray.200')} mb={0}>
                          Include in Training Data
                        </FormLabel>
                      </HStack>
                      <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                        Enable this to include this message in fine-tuning exports
                      </Text>
                    </FormControl>
                  </VStack>
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onQualityClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={saveQualityAssessment}
              isLoading={updateFetching}
              leftIcon={<CheckIcon />}
            >
              {selectedMessage?.adminFeedback?.markdown ? 'Update Assessment' : 'Save Assessment'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Export Modal */}
      <Modal isOpen={isExportOpen} onClose={onExportClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Export Training Data</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text mb={4}>
                Configure and export training data for fine-tuning. This will generate a JSONL file
                suitable for OpenAI fine-tuning.
              </Text>

              <Alert status="info" size="sm">
                <AlertIcon />
                <Text fontSize="sm">
                  Ready for training: {analytics?.trainingReady || 0} messages
                </Text>
              </Alert>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl>
                  <FormLabel>Export Type</FormLabel>
                  <Select
                    value={exportOptions.exportType}
                    onChange={(e) => handleExportOptionChange('exportType', e.target.value)}
                  >
                    <option value="standard">Standard (messages only)</option>
                    <option value="rlhf">RLHF (with quality scores)</option>
                    <option value="supervised">Supervised (Q&A pairs)</option>
                  </Select>
                  <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                    Choose the format for your fine-tuning approach
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Review Status Filter</FormLabel>
                  <Select
                    value={exportOptions.reviewStatus}
                    onChange={(e) => handleExportOptionChange('reviewStatus', e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="approved">Approved Only</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="pending">Pending</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Min Quality Score</FormLabel>
                  <NumberInput
                    min={1}
                    max={5}
                    value={exportOptions.minQualityScore || ''}
                    onChange={(value) => handleExportOptionChange('minQualityScore', value)}
                  >
                    <NumberInputField placeholder="1-5" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Max Quality Score</FormLabel>
                  <NumberInput
                    min={1}
                    max={5}
                    value={exportOptions.maxQualityScore || ''}
                    onChange={(value) => handleExportOptionChange('maxQualityScore', value)}
                  >
                    <NumberInputField placeholder="1-5" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <FormLabel>Date From</FormLabel>
                  <Input
                    type="date"
                    value={exportOptions.dateFrom}
                    onChange={(e) => handleExportOptionChange('dateFrom', e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Date To</FormLabel>
                  <Input
                    type="date"
                    value={exportOptions.dateTo}
                    onChange={(e) => handleExportOptionChange('dateTo', e.target.value)}
                  />
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <Flex align="center">
                  <Switch
                    id="reverse-qa"
                    isChecked={exportOptions.reverseQuestionAnswer}
                    onChange={(e) => handleExportOptionChange('reverseQuestionAnswer', e.target.checked)}
                    mr={3}
                  />
                  <FormLabel htmlFor="reverse-qa" mb={0}>
                    Reverse Question & Answer
                  </FormLabel>
                </Flex>
                <Text fontSize="xs" color={useColorModeValue('gray.600', 'gray.400')} mt={1}>
                  Swap user questions and AI responses. Useful for training models to generate questions from answers.
                </Text>
              </FormControl>

              {exportOptions.exportType && (
                <Alert status="info" size="sm">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="bold">Export Format Preview:</Text>
                    <Text fontSize="xs">
                      {exportOptions.exportType === 'standard' && 'Standard message format with user/assistant roles'}
                      {exportOptions.exportType === 'rlhf' && 'RLHF format with quality scores and feedback'}
                      {exportOptions.exportType === 'supervised' && 'Supervised learning format with input/output pairs'}
                    </Text>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onExportClose} isDisabled={exportFetching}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleExport}
              isLoading={exportFetching}
              loadingText="Exporting..."
            >
              Export & Download
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminFineTuning;